import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators,FormsModule, ReactiveFormsModule} from '@angular/forms';
import { AuthService } from '@core/services/auth.service';
import { ConstantsService } from '@core/services/constants.service';
import { ToastrService } from 'ngx-toastr';
import { SettingsService } from './settings.service';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/material.module';
import { SharedModule } from '@app/shared/shared.module';
import { MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-settings',
  standalone: true,
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    SharedModule
  ]
})
export class SettingsComponent implements OnInit,OnDestroy{
  private readonly userSubscription!: Subscription;
  clientToken: any;
  userToken: any;

  form!: FormGroup;
  formSubmitted = false;
  mailSettings: any;
  preDepartmentList: any;
  departmentList: any;
  IsTrainingAdmin: boolean = false;

  constructor(
    private readonly fb: FormBuilder,
    public service: SettingsService,
    public authService: AuthService,
    public constantService: ConstantsService,
    private readonly toastr: ToastrService,
    public dialogRef: MatDialogRef<SettingsComponent>,
  ) {
    this.clientToken = this.constantService.CLIENT_TOKEN;
    this.userToken = this.authService.getToken();
    this.userSubscription = this.authService.currentUser$.subscribe(userData => {
      this.IsTrainingAdmin = userData.IsTrainingAdmin;
    });
  }

  ngOnInit(): void {
    this.getDepartmentList();
    this.getMailSettings();

    //form validations
    this.form = this.fb.group({
      from: ['', [Validators.required, Validators.email]],
      subject: ['', Validators.required],
      host: ['', Validators.required],
      port: ['', Validators.required],
      user_name: ['', Validators.required],
      password: ['', Validators.required],
      ssl: ['', Validators.required]
    });

  }

  //get training mail settings
  getMailSettings() {
    this.service.getMailSettings().subscribe({
      next: (response) => {
        this.mailSettings = response;
        this.patchValues();
      },
      error: (error) => {
        console.log('getMailSettings error : ', error);
      }
    });

  }

  patchValues() {
    //bind data
    this.form.patchValue({
      from: this.mailSettings.SMTPFrom,
      subject: this.mailSettings.SMTPSubject,
      host: this.mailSettings.SMTPHost,
      port: this.mailSettings.SMTPPort,
      user_name: this.mailSettings.SMTPUserName,
      password: this.mailSettings.SMTPPassword,
      ssl: this.mailSettings.SMTPEnableSsl,
    });
  }

  // get list of departments
  getDepartmentList() {
    this.service.getDepartmentList().subscribe({
      next: (response) => {
        this.preDepartmentList = response;
      },
      error: (error) => {
        console.log('getDepartmentList error : ', error);
      }
    });
  }

  onSubmit() {
    this.formSubmitted = true;
    if (this.form.valid) {
      const formData = this.form.value;
      // Remove the pre_department check since it's commented out in the form
      const payload = {
        ClientToken: this.clientToken,
        SMTPEnableSsl: formData?.ssl,
        SMTPFrom: formData?.from,
        SMTPHost: formData?.host,
        SMTPPassword: formData?.password,
        SMTPPort: formData?.port,
        SMTPSubject: formData?.subject,
        SMTPUserName: formData?.user_name,
        TrainingSettingsNumber: this.mailSettings.MailSettingsNumber,
        UserToken: this.userToken,
      }

      //save mail settings
      this.service.saveMailSetting(payload).subscribe({
        next: (response) => {
         if (response === true) {
            this.toastr.clear();
            this.toastr.success('Training email settings UPDATED successfully.', 'Success', {
              positionClass: 'toast-bottom-left',
              toastClass: "toast-icon custom-toast-success",
            });
            this.closeDialog();
          } else {
            this.toastr.clear();
            this.toastr.error('Something went wrong', 'Error', {
              positionClass: 'toast-bottom-left',
              toastClass: "toast-icon custom-toast-error",
            });
          }
        },
        error: (error) => {
          console.log("saveMailSetting error : ", error);
        }
      });
    }
  }

  handlePreDepartmentChange() {
    this.departmentList = this.form.get('pre_department')?.value;
  }

  togglePreDepartmentCheck() {
    const preDepartmentControl = this.form.get('pre_department');
    const allPreDepartments = this.preDepartmentList?.map((dep: { Department: any; }) => dep.Department);
    if (this.isAllPreDepartmentsSelected()) {
      // Uncheck all
      preDepartmentControl?.patchValue([]);
    } else {
      // Check all
      preDepartmentControl?.patchValue(allPreDepartments);
    }
    this.handlePreDepartmentChange();
  }

  isAllPreDepartmentsSelected() {
    const preDepartmentControl = this.form.get('pre_department');
    const allPreDepartments = this.preDepartmentList?.map((dep: { Department: any; }) => dep.Department);
    return preDepartmentControl?.value?.length === allPreDepartments?.length;
  }

  toggleDepartmentCheck() {
    const departmentControl = this.form.get('department');
    const allDepartments = this.departmentList;
  
    if (this.isAllDepartmentsSelected()) {
      // Uncheck all
      departmentControl?.patchValue([]);
    } else {
      // Check all
      departmentControl?.patchValue(allDepartments);
    }
  }

  isAllDepartmentsSelected() {
    const departmentControl = this.form.get('department');
    const allDepartments = this.departmentList?.map((dep: { Department: any; }) => dep.Department);
    return departmentControl?.value?.length === allDepartments?.length;
  }

  //Open training settings dialog
  closeDialog() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
  }

}
